import { TdsAlert, TdsButton, TdsButtonContainer, TdsCheck, TdsField } from '@trv-tds/react';
import { GlobalContext } from 'context/GlobalProvider';
import _every from 'lodash/every';
import _isEmpty from 'lodash/isEmpty';
import _values from 'lodash/values';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { getBusRelationshipCodes, getPhoneTypeCodes } from '../../apis/pa-services';
import { CancelModal, Stepper } from '../../components/shared';
import { useReloadRedirect } from '../../hooks';
import { RESPONSES, STEPPER_TEXT, STEP_STATUS } from '../../utils/constants';
import {
    metricsCancelClickTag,
    metricsElementTag,
    metricsPage,
    metricsPageViewTag
} from '../../utils/coremetrics';
import {
    getEmailPatternAlertText,
    getMaxlengthAlertText,
    getMinlengthAlertText,
    getNamePatternAlertText,
    getPhonePatternAlertText,
    getRequiredAlertText
} from '../../utils/validation';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import ScrollToTop from '../../components/shared/ScrollToTop';

const steps = [
    {
        progress: STEP_STATUS.COMPLETE,
        text: STEPPER_TEXT.FIRST_STEP
    },
    {
        progress: STEP_STATUS.CURRENT,
        text: STEPPER_TEXT.SECOND_STEP
    },
    {
        progress: STEP_STATUS.INCOMPLETE,
        text: STEPPER_TEXT.THIRD_STEP
    },
    {
        progress: STEP_STATUS.INCOMPLETE,
        text: STEPPER_TEXT.FOURTH_STEP
    }
];

const ERROR_404 = RESPONSES.ERROR_404.error;

// Helper functions (module scope)
const getFieldAlert = (continueClicked, field) => (continueClicked && field ? 'error' : '');
const getFieldMessage = (continueClicked, field) => (continueClicked ? field : '');
const getConfirmAlert = (primaryFieldAlert, confirmFieldAlert) =>
    _isEmpty(primaryFieldAlert) && confirmFieldAlert ? 'error' : '';
const getConfirmMessage = (primaryFieldAlert, confirmFieldAlert) =>
    _isEmpty(primaryFieldAlert) ? confirmFieldAlert : '';
const isConfirmDisabled = primaryFieldAlert => !_isEmpty(primaryFieldAlert);
const showBusinessNameRowClass = isBusCdRepresentative =>
    'tds-row tds-justify-content-center' + (isBusCdRepresentative ? '' : ' tds-d-none');
const cleanPhoneNumber = number => number.replace(/[().\- ]/g, '');
const formatPhoneNumber = number => {
    const cleanNumber = cleanPhoneNumber(number);
    return `${cleanNumber.substring(0, 3)}-${cleanNumber.substring(3, 6)}-${cleanNumber.substring(
        6,
        10
    )}`;
};

// Additional render helpers (module scope)
const renderPhoneTypeField = (phoneTypeCdData, continueClicked, alerts, setPhoneType) =>
    phoneTypeCdData ? (
        <TdsField
            label="PHONE TYPE"
            alert={getFieldAlert(continueClicked, alerts.phoneType)}
            message={getFieldMessage(continueClicked, alerts.phoneType)}
            data-testid="phoneType"
        >
            <select
                name="phoneType"
                id="phoneType"
                data-testid="phoneType-test"
                defaultValue=""
                onChange={e => setPhoneType(e.target.value)}
            >
                <option value="">(Select One)</option>
                {phoneTypeCdData.map(code => (
                    <option key={code.valueCd} value={code.valueCd}>
                        {code.codeDescTxt}
                    </option>
                ))}
            </select>
        </TdsField>
    ) : (
        <div aria-disabled="true" aria-label="loading" style={{ lineHeight: 1 }}>
            <Skeleton width={100} height={15} />
            <Skeleton height={39} />
        </div>
    );

const renderBusRelationshipFieldset = (
    busRelationshipCdData,
    continueClicked,
    alerts,
    setBusRelationshipCd
) => (
    <fieldset role="group">
        <legend className="tds-h4">What is your relationship to this business</legend>
        {busRelationshipCdData ? (
            busRelationshipCdData.map(({ valueCd, codeDescTxt }, index) => (
                <TdsCheck
                    key={valueCd}
                    type="radio"
                    name="busRelationshipCd"
                    value={valueCd}
                    onTdsChange={e => setBusRelationshipCd(e.target.value)}
                    data-testid={'busRelationsipCd' + index}
                >
                    <span dangerouslySetInnerHTML={{ __html: codeDescTxt }}></span>
                </TdsCheck>
            ))
        ) : (
            <div aria-disabled="true" aria-label="loading">
                <Skeleton count={2} height={45} />
            </div>
        )}
        {continueClicked && alerts.busRelationshipCd ? (
            <TdsAlert type="error" inline data-testid="busRelationshipCd-error-msg">
                {alerts.busRelationshipCd}
            </TdsAlert>
        ) : (
            ''
        )}
    </fieldset>
);

const renderBusinessNameSection = (
    continueClicked,
    alerts,
    isBusCdRepresentative,
    businessNm,
    setBusinessNm
) => (
    <div className={showBusinessNameRowClass(isBusCdRepresentative())}>
        <div className="tds-col-12">
            <TdsField
                label="BUSINESS NAME"
                alert={getFieldAlert(continueClicked, alerts.businessNm)}
                message={getFieldMessage(continueClicked, alerts.businessNm)}
                data-testid="businessNm"
            >
                <input
                    type="text"
                    id="businessNm"
                    name="businessNm"
                    aria-required="true"
                    onChange={e => setBusinessNm(e.target.value)}
                    value={businessNm}
                />
            </TdsField>
        </div>
    </div>
);

export default function ContactInformation() {
    const {
        actions: { updateContactInfo, throwError }
    } = useContext(GlobalContext);

    useReloadRedirect();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneType, setPhoneType] = useState('');
    const [busRelationshipCd, setBusRelationshipCd] = useState('');
    const [businessNm, setBusinessNm] = useState('');
    const [alerts, setAlerts] = useState({
        firstName: '',
        lastName: '',
        email: '',
        confirmEmail: '',
        jobTitle: '',
        phoneNumber: '',
        phoneType: '',
        busRelationshipCd: '',
        businessNm: ''
    });
    const [phoneTypeCdData, setPhoneTypeCdData] = useState(null);
    const [busRelationshipCdData, setBusRelationshipCdData] = useState(null);
    const [continueClicked, setContinueClicked] = useState(false);
    const navigate = useNavigate();

    const isBusCdRepresentative = useCallback(() => busRelationshipCd === '2', [busRelationshipCd]);

    useEffect(() => {
        metricsPageViewTag(metricsPage.contactInfo);
    }, []);

    useEffect(() => {
        (async () => {
            const [{ data: phoneTypeCds }, { data: busRelationshipCds }] = await Promise.all([
                getPhoneTypeCodes(),
                getBusRelationshipCodes()
            ]);
            if (phoneTypeCds && busRelationshipCds) {
                setPhoneTypeCdData(phoneTypeCds);
                setBusRelationshipCdData(busRelationshipCds);
            } else throwError('404', ERROR_404);
        })();
    }, [throwError]);

    useEffect(() => {
        setAlerts({
            firstName:
                getRequiredAlertText(firstName) ||
                getNamePatternAlertText(firstName) ||
                getMaxlengthAlertText(firstName, 38),
            lastName:
                getRequiredAlertText(lastName) ||
                getNamePatternAlertText(lastName) ||
                getMaxlengthAlertText(lastName, 38),
            email:
                getRequiredAlertText(email) ||
                getMinlengthAlertText(email, 6) ||
                getMaxlengthAlertText(email, 50) ||
                getEmailPatternAlertText(email),
            confirmEmail: email === confirmEmail ? '' : 'Email does not match.',
            phoneNumber: getRequiredAlertText(phoneNumber) || getPhonePatternAlertText(phoneNumber),
            phoneType: getRequiredAlertText(phoneType),
            jobTitle: getRequiredAlertText(jobTitle) || getMaxlengthAlertText(jobTitle, 30),
            busRelationshipCd: getRequiredAlertText(busRelationshipCd),
            businessNm: isBusCdRepresentative()
                ? getRequiredAlertText(businessNm) || getMaxlengthAlertText(businessNm, 38)
                : ''
        });
    }, [
        firstName,
        lastName,
        email,
        confirmEmail,
        jobTitle,
        phoneNumber,
        phoneType,
        busRelationshipCd,
        businessNm,
        isBusCdRepresentative
    ]);

    const formValid = () => _every(_values(alerts), alertText => _isEmpty(alertText));

    const updateContext = () => {
        updateContactInfo({
            firstNm: firstName,
            lastNm: lastName,
            emailAddressTxt: email,
            jobTitleTxt: jobTitle,
            phoneNbr: formatPhoneNumber(phoneNumber),
            phoneTypeCd: phoneType,
            businessRelationshipTypeCd: busRelationshipCd,
            businessNm: isBusCdRepresentative() ? businessNm : null
        });
    };

    const handleContinue = () => {
        metricsElementTag(metricsPage.contactInfo, false);
        setContinueClicked(true);
        if (!formValid()) return;
        updateContext();
        navigate('/profile');
    };

    return (
        <div className="contact-info">
            <ScrollToTop />
            <Stepper steps={steps}></Stepper>
            <h1 data-testid="contact-info-header" className="tds-h1--article reg-header center">
                Contact Information
            </h1>
            <form>
                <p className="tds-row tds-justify-content-center page-caption">
                    Verify the information below and make any changes if needed
                </p>
                <div className="tds-row tds-justify-content-center tds-mb-m">
                    <div className="tds-col-12">
                        <TdsField
                            label="FIRST NAME"
                            alert={getFieldAlert(continueClicked, alerts.firstName)}
                            message={getFieldMessage(continueClicked, alerts.firstName)}
                            data-testid="firstName"
                        >
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                aria-required="true"
                                onChange={e => setFirstName(e.target.value)}
                                autoComplete="given-name"
                            />
                        </TdsField>
                        <TdsField
                            label="LAST NAME"
                            alert={getFieldAlert(continueClicked, alerts.lastName)}
                            message={getFieldMessage(continueClicked, alerts.lastName)}
                            data-testid="lastName"
                        >
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                aria-required="true"
                                onChange={e => setLastName(e.target.value)}
                                autoComplete="family-name"
                            />
                        </TdsField>
                        <TdsField
                            label="EMAIL ADDRESS"
                            alert={getFieldAlert(continueClicked, alerts.email)}
                            message={getFieldMessage(continueClicked, alerts.email)}
                            data-testid="email"
                        >
                            <input
                                type="text"
                                id="email"
                                name="email"
                                aria-required="true"
                                onChange={e => setEmail(e.target.value)}
                                autoComplete="email"
                            />
                        </TdsField>
                        <TdsField
                            label="CONFIRM EMAIL ADDRESS"
                            alert={getConfirmAlert(alerts.email, alerts.confirmEmail)}
                            message={getConfirmMessage(alerts.email, alerts.confirmEmail)}
                            data-testid="confirmEmail"
                        >
                            <input
                                type="text"
                                id="confirmEmail"
                                name="confirmEmail"
                                aria-required="true"
                                disabled={isConfirmDisabled(alerts.email)}
                                onChange={e => setConfirmEmail(e.target.value)}
                                autoComplete="off"
                            />
                        </TdsField>
                        <div className="tds-row tds-justify-content-center">
                            <div className="tds-col-md-8 tds-col-sm-12">
                                <TdsField
                                    label="PHONE NUMBER"
                                    alert={getFieldAlert(continueClicked, alerts.phoneNumber)}
                                    message={getFieldMessage(continueClicked, alerts.phoneNumber)}
                                    data-testid="phoneNumber"
                                >
                                    <input
                                        type="text"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        aria-required="true"
                                        onChange={e => setPhoneNumber(e.target.value)}
                                        autoComplete="tel"
                                    />
                                </TdsField>
                            </div>
                            <div className="tds-col-md-4 tds-col-sm-12">
                                {renderPhoneTypeField(
                                    phoneTypeCdData,
                                    continueClicked,
                                    alerts,
                                    setPhoneType
                                )}
                            </div>
                        </div>
                        <TdsField
                            label="JOB TITLE"
                            alert={getFieldAlert(continueClicked, alerts.jobTitle)}
                            message={getFieldMessage(continueClicked, alerts.jobTitle)}
                            data-testid="jobTitle"
                        >
                            <input
                                type="text"
                                id="jobTitle"
                                name="jobTitle"
                                aria-required="true"
                                onChange={e => setJobTitle(e.target.value)}
                            />
                        </TdsField>
                    </div>
                </div>
                <div className="tds-row tds-justify-content-center business-type">
                    <div className="tds-col-12">
                        {renderBusRelationshipFieldset(
                            busRelationshipCdData,
                            continueClicked,
                            alerts,
                            setBusRelationshipCd
                        )}
                    </div>
                </div>
                {renderBusinessNameSection(
                    continueClicked,
                    alerts,
                    isBusCdRepresentative,
                    businessNm,
                    setBusinessNm
                )}
                <TdsButtonContainer class="button-container-fix">
                    <TdsButton variant="primary" onClick={handleContinue}>
                        Continue
                    </TdsButton>
                    <TdsButton
                        variant="secondary"
                        data-trigger-modal="cancel-modal"
                        onClick={() => metricsCancelClickTag()}
                    >
                        Cancel
                    </TdsButton>
                </TdsButtonContainer>
            </form>
            <CancelModal />
        </div>
    );
}
